// This list is just used to validate sorting options from URL parameters
// See snippets/collection-grid-top-v3.liquid for supported filters
const supportedURLSortingOptions = [
  'manual',
  // 'most-wishlisted',
  'price-ascending',
  'price-descending',
  // 'title-ascending',
  // 'title-descending',
  'created-ascending',
  'created-descending',
  // 'popularity',
];

// https://shopify.dev/docs/storefronts/themes/navigation-search/filtering/storefront-filtering
const supportedURLFilterFields = [
  // 0 - Variants that are out of stock. 1 - Variants that are in stock. 0,1 - Variants of either stock status.
  // // Not compatible with our Nosto availability filtering
  // {
  //   urlParam: 'v.availability',
  //   internalField: 'availability',
  // },
  // // A single monetary value in the format of the shop's default currency. For example 5 or 20.40
  // // Not compatible with our Nosto price range filtering
  // {
  //   urlParam: 'v.price.gte',
  //   internalField: 'price',
  // },
  // // A single monetary value in the format of the shop's default currency. For example 5 or 20.40
  // // Not compatible with our Nosto price range filtering
  // {
  //   urlParam: 'v.price.lte',
  //   internalField: 'price',
  // },
  // A single product type, or a comma-separated list of product types. For example shoes, or shoes,belts
  {
    urlParam: 'p.product_type',
    internalField: 'customFields.producttype',
  },
  // A single price range option in the format min-max or comma-separated list of price range options. For example: 0-25,25-50,50-75
  // Not compatible with Shopify legacy filtering. Use v.price.gte / v.price.lte parameters instead.
  {
    urlParam: 'v.price_range',
    internalField: 'price',
  },
  // A single size option or comma-separated list of size options. For example: S,M,L
  {
    urlParam: 'v.option.size',
    internalField: 'skus.customFields.size',
  },
  // 0 - Products that are not marked as plus size. 1 - Products that are marked as plus size. 0,1 - Products of either status.
  {
    urlParam: 'v.m.astr.plus_size',
    internalField: 'skus.customFields.astr-plus_size',
  },
  // A single primary color family or comma-separated list of primary color families. For example: red,blue
  {
    urlParam: 'v.m.astr.color_family',
    internalField: 'skus.customFields.astr-color_family',
  },
  // A single secondary color family or comma-separated list of secondary color families. For example: red,blue
  {
    urlParam: 'v.m.astr.color_family_secondary',
    internalField: 'skus.customFields.astr-color_family_secondary',
  },
  // A single product tag, or a comma-separated list of product tags. For example new, or new,trending.
  // In our case, we mstly use it for custom filter options such as vt::Dresses::Neckline,vt::Dresses::Dress Length
  {
    urlParam: 'p.tag',
    internalField: 'tags1',
  }
];

/**
 * Get the URL parameter name for a given internal field name
 * 
 * @param {string} internalField 
 * 
 * @returns {string} URL parameter name
 */
const getURLParamFromInternalField = (internalField) => {
  const found = supportedURLFilterFields.find(field => field.internalField === internalField);

  // Custom filters (like product tags) may have 'vt::' prefixes
  if (!found && internalField.includes('vt::')) {
    return 'filter.p.custom.' + internalField.replaceAll('::', '--');
  }

  return found ? 'filter.' + found.urlParam : internalField;
};

/**
 * Get the internal field name for a given URL parameter name
 * 
 * @param {string} urlParam 
 * 
 * @returns {string} Internal field name
 */
const getInternalFieldFromURLParam = (urlParam) => {
  const found = supportedURLFilterFields.find(field => field.urlParam === urlParam);

  // Custom filters (like product tags) may have 'vt--' prefixes
  if (!found && urlParam.includes('vt--')) {
    return urlParam.replace('p.custom.', '').replaceAll('--', '::');
  }

  return found ? found.internalField : urlParam;
};

/**
 * Get filters and sorting options from URL parameters
 * 
 * Shopify documentation: https://shopify.dev/docs/storefronts/themes/navigation-search/filtering/storefront-filtering#url-parameter-structure
 * 
 * Example: ?filter.v.availability=1&filter.p.tag=grey&filter.v.price.gte=10.00&filter.v.price.lte=20&sort_by=manual
 * 
 * @returns {Object} Filters from URL parameters and sorting matching the format used in wild-filter localStorage
 * Example:
 * {
 *  "skus.customFields.astr-color_family": [
 *   "black",
 *   "blue"
 *  ],
 *  "vt::Dresses::Dress Length": [
 *    "Midi"
 *  ],
 *  "price": [
 *    "50-75",
 *    "75-100"
 *  ],
 *  sort_by: 'manual',
 *  page: 1
 * }
 */
const getURLFiltersAndSorting = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const urlData = {};
  let sorting = 'manual';
  let page = 1;
  let hasFiltersOrSorting = false;

  urlParams.forEach((value, key) => {
    if (key.startsWith('filter.')) {
      const field = key.replace('filter.', '');
      let supportedField = supportedURLFilterFields.find(f => f.urlParam === field);

      if (field.includes('vt--')) {
        supportedField = {
          urlParam: field,
          internalField: field.replace('p.custom.', '').replaceAll('--', '::'),
        };
      }

      if (!supportedField) return;

      let values = value.split(',');

      // Ensure the values exist as an option within the desktop filters
      const internalField = getInternalFieldFromURLParam(field);
      // console.log('[ASTR] Validating filter values from URL for field', internalField, values);
      const filterOptionsElement = document.querySelector(`[data-collection-grid-container] [data-filter-options-type="${internalField}"]`);
      if (!filterOptionsElement) return;

      const availableOptions = Array.from(filterOptionsElement.querySelectorAll('[data-option-value]')).map(el => el.getAttribute('data-option-value'));
      const filteredValues = values.filter(v => availableOptions.includes(v));
      if (filteredValues.length === 0) return;
      // console.log('[ASTR] Valid filter values from URL for field', internalField, filteredValues);

      // Use only the validated values
      values = filteredValues;

      // Assign array of values to the internalField key
      if (!urlData[supportedField.internalField]) {
        urlData[supportedField.internalField] = [];
      }
      urlData[supportedField.internalField] = urlData[supportedField.internalField].concat(values);
      hasFiltersOrSorting = true;
    } else if (key === 'sort_by' && supportedURLSortingOptions.includes(value)) {
      sorting = value;
      hasFiltersOrSorting = true;
    } else if (key === 'page') {
      page = parseInt(value, 10) || 1;
    }
  });

  urlData.sort_by = sorting;
  urlData.page = page;

  if (!hasFiltersOrSorting) {
    return null;
  }

  console.log('[ASTR] 1 - selected_filtering from URL', urlData);
  return urlData;
};

/**
 * Remove a filter option from the URL parameters after unselecting it
 * 
 * @param {string} filter_type 
 * @param {string} filter_value 
 * 
 * @returns {void}
 */
const removeFilterFromURL = (filter_type, filter_value) => {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  // Find the corresponding URL parameter for the given filter_type
  const urlParam = getURLParamFromInternalField(filter_type);
  console.log('[ASTR] Remove filter from URL', filter_type, urlParam, filter_value);

  // Get existing filter values (comma-separated string)
  const existing_value = params.get(urlParam);

  if (!existing_value) return;

  const values = existing_value.split(',');

  // Remove the specific filter value
  const updated_values = values.filter(value => value !== filter_value);

  // Clear existing param
  params.delete(urlParam);

  if (updated_values.length > 0) {
    // Re-add param with remaining values
    params.append(urlParam, updated_values.join(','));
  }

  // Update the URL without reloading the page
  window.history.replaceState({}, '', url.pathname + '?' + params.toString());
};

/**
 * Add a filter to the URL parameters after selecting it
 * 
 * @param {string} filter_type 
 * @param {string} filter_value
 * 
 * @returns {void}
 */
const addFilterToURL = (filter_type, filter_value) => {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  // Use urlParam for the query string key
  const urlParam = getURLParamFromInternalField(filter_type);

  // Get existing value for this filter
  const existing_value = params.get(urlParam);

  // Append the new filter value
  if (existing_value) {
    const values = existing_value.split(',');
    if (!values.includes(filter_value)) {
      values.push(filter_value);
      params.set(urlParam, values.join(','));
    }
  } else {
    params.append(urlParam, filter_value);
  }

  // Update the URL without reloading the page
  window.history.replaceState({}, '', url.pathname + '?' + params.toString());
};

/**
 * Clear all filter.* and sort_by parameters from the URL
 * 
 * @returns {void}
 */
const clearAllURLFiltersAndSorting = () => {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  // Collect keys to remove first
  const keysToRemove = [];
  params.forEach((value, key) => {
    if (key.startsWith('filter.') || key === 'sort_by') {
      keysToRemove.push(key);
    }
  });

  // Remove them in a separate loop
  keysToRemove.forEach(key => {
    params.delete(key);
  });

  // Update the URL without reloading the page
  window.history.replaceState({}, '', url.pathname + '?' + params.toString());
};

/**
 * Add sorting option to the URL parameters after selecting it
 * 
 * @param {string} sorting 
 * 
 * @returns {void}
 */
const addSortingToURL = (sorting) => {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  // Set or update the sort_by parameter
  params.set('sort_by', sorting);

  // Update the URL without reloading the page
  window.history.replaceState({}, '', url.pathname + '?' + params.toString());
};

/**
 * Update the current collection page in the URL after selecting a filter or a pagination item
 * 
 * @param {number} page 
 */
const updatePageInUrl = (page) => {
  const url = new URL(window.location.href);
  url.searchParams.set('page', page);
  window.history.replaceState({}, '', url);
};

export {
  getURLFiltersAndSorting,
  removeFilterFromURL,
  addFilterToURL,
  clearAllURLFiltersAndSorting,
  addSortingToURL,
  updatePageInUrl,
};
